import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { PreAuthRouteDecision, usePreAuthRoute } from '@rapid-recovery-agency-inc/insightt-graphql';

// NOTE: `./types` is imported with an explicit `.ts` extension — this directory also has a legacy
// `types.js` (unrelated `SET_DEVICE_TOKEN_REQUEST` action type, consumed by `actions.js`).
// Vite/vitest's default module resolution order checks `.js` before `.ts`, so the extensionless
// specifier would silently resolve to `types.js` at runtime. See constants.ts/auth-controller.ts
// for the same note.
import { PreAuthAttemptStatus } from './types.ts';
import { getAppAuthBoundary } from './auth-boundary';

/**
 * Whether the auth boundary holds a session, SSO or legacy (FR-007, FR-008; T070). Re-renders on
 * sign-in, renewal failure and cleanup. Use this instead of reading `state.auth.token` or the
 * `access_token` cookie, which only ever reflect a legacy session.
 */
export function useIsAuthenticated(): boolean {
  const boundary = getAppAuthBoundary();
  return useSyncExternalStore(boundary.subscribe, boundary.isAuthenticated);
}

/**
 * `usePreAuthRoute` consumption wrapper (T018; research.md §3; data-model.md §3; FR-001/FR-002/
 * FR-016/FR-018/FR-019/FR-032).
 *
 * The published `usePreAuthRoute` (`@rapid-recovery-agency-inc/insightt-graphql`, #3885) wraps
 * Apollo's `useLazyQuery` and exposes `{ fetchPreAuthRoute, loading, route, error }`. Calling
 * `fetchPreAuthRoute(email)` returns Apollo's own `Promise<QueryResult>`, which settles once with
 * the request outcome — so this wrapper drives its state machine off that promise rather than off
 * the reactive `loading`/`route`/`error` triple, which only reflects the *last* call and would
 * race across overlapping requests. `useGatewayPreAuthRoute` adds everything #3885 intentionally
 * does not own (research.md §3: "#3885 owns the query and enum; Dashboard owns browser
 * orchestration"):
 *
 * 1. A 10-second deadline per attempt, enforced by a wrapping timer (not Apollo's own network
 *    timeout, which is not configurable per-call here) — `Promise.race`d against the query promise
 *    so a hung request cannot hang the caller.
 * 2. Exactly one retry, and only for a transient connectivity failure (`ApolloError.networkError`
 *    set with no `graphQLErrors`) — a GraphQL/business error or a timeout is not retried.
 * 3. Duplicate suppression: calling `requestRoute` again with the same email string while that
 *    exact request is still in flight is a no-op — it does not start a second underlying request.
 *    (The wrapper does not itself normalize/validate the email — FR-002 assigns that to the
 *    gateway input; callers are expected to pass the already-normalized value, so the dedup key is
 *    simply that string.)
 * 4. Cancellation and stale-result rejection: a call with a *different* email always starts a new
 *    attempt and immediately supersedes any older one. A local monotonically increasing generation
 *    counter (not data-model.md §3's Redux-visible `operationGeneration`, which T020 assigns via
 *    T016's `LatestGenerationEpisode` when it wires this hook into the gateway/Redux) marks the
 *    previous attempt stale; its resolution — whether success, error, or timeout — is discarded if
 *    it arrives after a newer attempt has started, or after its own attempt already settled (e.g. a
 *    timeout fires and is then followed by the underlying promise finally resolving).
 *
 * `usePreAuthRoute` itself has no cancellation hook (research.md notes #3885 deliberately does not
 * adopt an abort-signal convention), so a "cancelled" underlying request keeps running to
 * completion over the network; this wrapper only guarantees its *result* can never be surfaced or
 * overwrite a newer one.
 */

/** Wrapping deadline for one `usePreAuthRoute` attempt (FR-016; research.md §3). */
export const PRE_AUTH_ROUTE_TIMEOUT_MS = 10_000;

/** Exactly one retry is allowed, and only for a transient connectivity failure (FR-016). */
export const PRE_AUTH_ROUTE_MAX_TRANSIENT_RETRIES = 1;

/**
 * Why an attempt settled as `FAILED` (data-model.md §3: "Missing, malformed, unknown, error, and
 * timeout outcomes settle at retry/recovery"). Not part of the Redux-visible `AuthPreAuthAttemptStatus`
 * enum (types.ts) because that enum only tracks lifecycle, not failure cause; this is presentation
 * detail local to this hook that a consumer (T019) may use to select copy.
 */
export enum PreAuthRouteFailureReason {
  /** The 10-second deadline elapsed before the request settled. */
  TIMEOUT = 'TIMEOUT',
  /** A transient connectivity failure occurred and the single allowed retry also failed. */
  NETWORK_ERROR = 'NETWORK_ERROR',
  /** The server returned a GraphQL/business error (not a connectivity failure). */
  GRAPHQL_ERROR = 'GRAPHQL_ERROR',
  /** The response was missing the decision field entirely. */
  MISSING = 'MISSING',
  /** The response carried a value outside the published `PreAuthRouteDecision` enum. */
  UNKNOWN_DECISION = 'UNKNOWN_DECISION',
}

/** Outcome surfaced to consumers of {@link useGatewayPreAuthRoute}. */
export interface GatewayPreAuthRouteResult {
  readonly status: PreAuthAttemptStatus;
  readonly decision: PreAuthRouteDecision | null;
  readonly reason: PreAuthRouteFailureReason | null;
}

const IDLE_RESULT: GatewayPreAuthRouteResult = {
  status: PreAuthAttemptStatus.IDLE,
  decision: null,
  reason: null,
};

const VALID_DECISIONS: ReadonlySet<string> = new Set(Object.values(PreAuthRouteDecision));

/** Sentinel returned by the wrapping timeout race branch. */
const TIMEOUT_SENTINEL = Symbol('pre-auth-route-timeout');

/**
 * True only for a transient connectivity failure: Apollo populates `networkError` when the
 * request itself could not be completed (offline, DNS, connection reset, non-2xx transport
 * failure). `graphQLErrors` means the server was reached and responded with a business/validation
 * error, which must fail closed rather than retry (FR-016, FR-019: never infer/retry into legacy).
 */
function isTransientNetworkError(error: unknown): boolean {
  const apolloLike = error as { networkError?: unknown; graphQLErrors?: unknown[] } | null | undefined;
  return Boolean(apolloLike?.networkError) && !(apolloLike?.graphQLErrors && apolloLike.graphQLErrors.length > 0);
}

/** Minimal shape this wrapper needs from a `usePreAuthRoute`-style hook, for testability. */
type FetchPreAuthRoute = (email: string) => Promise<{
  data?: { preAuthRoute?: { route?: PreAuthRouteDecision } } | null;
  error?: unknown;
}>;

interface AttemptRecord {
  readonly generation: number;
  readonly email: string;
  settled: boolean;
}

/**
 * Consumes the published `usePreAuthRoute` and returns `{ result, requestRoute }`. `requestRoute`
 * is fire-and-forget from the caller's perspective (matching how a gateway component starts a
 * submission and observes `result` reactively); it is not itself async so a caller cannot
 * accidentally await a promise that resolves to a stale outcome.
 */
export function useGatewayPreAuthRoute(): {
  result: GatewayPreAuthRouteResult;
  requestRoute: (email: string) => void;
} {
  const { fetchPreAuthRoute } = usePreAuthRoute();
  const [result, setResult] = useState<GatewayPreAuthRouteResult>(IDLE_RESULT);

  const generationRef = useRef(0);
  const activeAttemptRef = useRef<AttemptRecord | null>(null);
  const fetchRef = useRef<FetchPreAuthRoute>(fetchPreAuthRoute as unknown as FetchPreAuthRoute);
  useEffect(() => {
    fetchRef.current = fetchPreAuthRoute as unknown as FetchPreAuthRoute;
  }, [fetchPreAuthRoute]);

  // Any attempt still running when this hook unmounts must not be able to call `setState` later.
  //
  // The setup function must set `true` (not just register the `false` cleanup): React 18
  // StrictMode intentionally double-invokes mount effects in development (mount → run effect →
  // simulate unmount → run cleanup → simulate remount → run effect again) to surface exactly this
  // class of bug. Without this line, that dev-only simulated-unmount cleanup permanently latches
  // `isMountedRef.current` to `false` for the rest of the component's real, visible lifetime — a
  // `useRef(true)` initializer only runs once and is never reached again — so every subsequent
  // `applyIfCurrent` call silently discards its result via the `!isMountedRef.current` branch
  // below, and the gateway hangs on "Checking your email…" forever (the wrapping 10-second timeout
  // is equally discarded, since it also settles through `applyIfCurrent`). Only surfaces under
  // `npm run dev`'s StrictMode; a production `vite build`/`vite preview` never double-invokes
  // effects, which is why this went unnoticed until this task's real-backend `npm run dev` testing.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const applyIfCurrent = useCallback((attempt: AttemptRecord, next: GatewayPreAuthRouteResult) => {
    if (attempt.settled) {
      return;
    }
    attempt.settled = true;
    if (!isMountedRef.current || activeAttemptRef.current !== attempt) {
      // A newer attempt has already superseded this one, or the component unmounted: the older
      // (now-stale) outcome must never be surfaced (data-model.md §3, FR-018).
      return;
    }
    setResult(next);
  }, []);

  // A plain (non-memoized) function declaration, not a `useCallback` value: it recurses into
  // itself for the single allowed retry, and a function declaration's hoisting lets it reference
  // its own name directly rather than through a `useCallback` binding that is not yet assigned
  // at the point of the recursive call.
  async function runAttempt(attempt: AttemptRecord, email: string, retriesLeft: number): Promise<void> {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<typeof TIMEOUT_SENTINEL>((resolve) => {
      timeoutHandle = setTimeout(() => resolve(TIMEOUT_SENTINEL), PRE_AUTH_ROUTE_TIMEOUT_MS);
    });

    let outcome: Awaited<ReturnType<FetchPreAuthRoute>> | typeof TIMEOUT_SENTINEL;
    try {
      outcome = await Promise.race([fetchRef.current(email), timeoutPromise]);
    } catch (thrown) {
      clearTimeout(timeoutHandle);
      if (retriesLeft > 0 && isTransientNetworkError(thrown)) {
        await runAttempt(attempt, email, retriesLeft - 1);
        return;
      }
      applyIfCurrent(attempt, {
        status: PreAuthAttemptStatus.FAILED,
        decision: null,
        reason: isTransientNetworkError(thrown)
          ? PreAuthRouteFailureReason.NETWORK_ERROR
          : PreAuthRouteFailureReason.GRAPHQL_ERROR,
      });
      return;
    }
    clearTimeout(timeoutHandle);

    if (outcome === TIMEOUT_SENTINEL) {
      applyIfCurrent(attempt, {
        status: PreAuthAttemptStatus.FAILED,
        decision: null,
        reason: PreAuthRouteFailureReason.TIMEOUT,
      });
      return;
    }

    if (outcome.error) {
      if (retriesLeft > 0 && isTransientNetworkError(outcome.error)) {
        await runAttempt(attempt, email, retriesLeft - 1);
        return;
      }
      applyIfCurrent(attempt, {
        status: PreAuthAttemptStatus.FAILED,
        decision: null,
        reason: isTransientNetworkError(outcome.error)
          ? PreAuthRouteFailureReason.NETWORK_ERROR
          : PreAuthRouteFailureReason.GRAPHQL_ERROR,
      });
      return;
    }

    const route = outcome.data?.preAuthRoute?.route;
    if (route === undefined || route === null) {
      applyIfCurrent(attempt, {
        status: PreAuthAttemptStatus.FAILED,
        decision: null,
        reason: PreAuthRouteFailureReason.MISSING,
      });
      return;
    }
    if (!VALID_DECISIONS.has(route)) {
      applyIfCurrent(attempt, {
        status: PreAuthAttemptStatus.FAILED,
        decision: null,
        reason: PreAuthRouteFailureReason.UNKNOWN_DECISION,
      });
      return;
    }

    applyIfCurrent(attempt, {
      status: PreAuthAttemptStatus.DECIDED,
      decision: route,
      reason: null,
    });
  }

  // Also a plain function (not `useCallback`): it closes over `runAttempt`, which is itself
  // recreated every render (see above), so memoizing this one buys no referential stability.
  function requestRoute(email: string): void {
    const active = activeAttemptRef.current;
    if (active && !active.settled && active.email === email) {
      // Duplicate suppression: an identical submission is already in flight (FR-002).
      return;
    }

    generationRef.current += 1;
    const attempt: AttemptRecord = { generation: generationRef.current, email, settled: false };
    activeAttemptRef.current = attempt;

    setResult({ status: PreAuthAttemptStatus.DECIDING, decision: null, reason: null });

    void runAttempt(attempt, email, PRE_AUTH_ROUTE_MAX_TRANSIENT_RETRIES);
  }

  return { result, requestRoute };
}
